// Phase 33 — ⌘+K Command Palette.
//
// Always mounted at the App root. Toggled by ⌘+K (Mac) or Ctrl+K
// (Windows/Linux). Closes on Esc or backdrop click. Fuzzy search via the
// cmdk library (the same library Linear / Vercel / Raycast use).
//
// Sections rendered (visibility may vary based on role and current route):
//   • Navigate      — top-level routes
//   • Recent deals  — top 5 by updated_at, fetched once per open
//   • Actions       — context-aware (only the ones relevant to current path)
//   • Admin         — only if user.is_admin
//   • Settings      — language toggle, sign out
//
// Bilingual via i18next.
//
// Apple-spotlight feel: centered modal, dark backdrop, soft shadow,
// rounded-xl. No gradients on the chrome — clean white card on white bg.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Command } from "cmdk";
import { toast } from "sonner";
import {
  LayoutDashboard, Plus, Briefcase, GitCompare, Bot, FileBarChart2,
  Shield, Settings as SettingsIcon, LogOut, Languages, Home as HomeIcon,
  Search, ArrowRight, KeyRound, Sparkles,
  // Phase 34c.3 — syndication action icons
  Users2, Wallet, Coins, Link2,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { propertyDetailLineCompact } from "@/components/property/PropertyMeta";

const KEYBINDING_HINT = (() => {
  if (typeof navigator === "undefined") return "Ctrl + K";
  return /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘ + K" : "Ctrl + K";
})();

function _isHotkey(e) {
  return e.key === "k" && (e.metaKey || e.ctrlKey);
}

export default function CommandPalette() {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const isSpanish = (i18n.language || "en").toLowerCase().startsWith("es");
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [recentDeals, setRecentDeals] = useState([]);
  const inputRef = useRef(null);

  // Localised labels — kept inline so the palette ships without forcing
  // every consumer of i18n to know all these keys.
  const L = useMemo(() => isSpanish ? {
    placeholder: "Buscar acciones, ofertas, configuraciones…",
    nav: "Navegar",
    actions: "Acciones",
    admin: "Administración",
    settings: "Ajustes",
    recentDeals: "Ofertas recientes",
    empty: "Sin coincidencias.",
    hintNav: "↑↓ moverse",
    hintEnter: "↵ seleccionar",
    hintEsc: "Esc cerrar",
    dashboard: "Panel",
    newDeal: "Nueva oferta",
    portfolio: "Portafolio",
    compare: "Comparar",
    copilot: "Copiloto IA",
    reports: "Informes",
    admin_home: "Admin",
    settings_label: "Ajustes",
    toggleLang: "Cambiar idioma (EN ↔ ES)",
    signOut: "Cerrar sesión",
    markBought: "Marcar como comprada",
    exportCompare: "Exportar comparación a Excel",
    searchUsers: "Buscar usuarios…",
    auditLog: "Registro de auditoría",
    aiUsage: "Uso de IA",
    askCopilot: "Pregunta al copiloto",
    continueCopilot: "Continuar última conversación del copiloto",
    yearEndReport: "Generar informe de fin de año",
    langSwitchedEn: "Idioma cambiado a inglés.",
    langSwitchedEs: "Idioma cambiado a español.",
    signingOut: "Cerrando sesión…",
  } : {
    placeholder: "Search actions, deals, settings…",
    nav: "Navigate",
    actions: "Actions",
    admin: "Admin",
    settings: "Settings",
    recentDeals: "Recent deals",
    empty: "No matches.",
    hintNav: "↑↓ to navigate",
    hintEnter: "↵ to select",
    hintEsc: "Esc to close",
    dashboard: "Dashboard",
    newDeal: "New deal",
    portfolio: "Portfolio",
    compare: "Compare",
    copilot: "AI Copilot",
    reports: "Reports",
    admin_home: "Admin",
    settings_label: "Settings",
    toggleLang: "Toggle language (EN ↔ ES)",
    signOut: "Sign out",
    markBought: "Mark current deal as bought",
    exportCompare: "Export comparison to Excel",
    searchUsers: "Search users…",
    auditLog: "Open audit log",
    aiUsage: "AI usage dashboard",
    askCopilot: "Ask Copilot",
    continueCopilot: "Continue last Copilot conversation",
    yearEndReport: "Generate year-end report",
    // Phase 34c.3 — syndication
    investorPortal: "Go to Investor Portal",
    createPartnership: "Create partnership on this deal",
    computeDistribution: "Compute distribution",
    copyInviteLink: "Copy invite link for last pending member",
    inviteLinkCopied: "Invite link copied",
    inviteLinkNoneAvailable: "No pending invites on this partnership",
    inviteLinkError: "Could not fetch invite link",
    langSwitchedEn: "Language switched to English.",
    langSwitchedEs: "Language switched to Spanish.",
    signingOut: "Signing out…",
  }, [isSpanish]);

  // Global hotkey
  useEffect(() => {
    const onKey = (e) => {
      if (_isHotkey(e)) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape" && open) {
        e.preventDefault();
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Pre-warm recent deals on first open so the section is non-empty
  useEffect(() => {
    if (!open || !user) return;
    if (recentDeals.length > 0) return;
    api.get("/deals", { params: { limit: 5 } })
      .then((res) => {
        const list = Array.isArray(res.data)
          ? res.data
          : (res.data?.items || res.data?.deals || []);
        setRecentDeals(list.slice(0, 5));
      })
      .catch(() => { /* non-blocking — recents are nice-to-have */ });
  }, [open, user, recentDeals.length]);

  // Focus the input when the palette opens.
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 20);
    } else {
      setQuery("");
    }
  }, [open]);

  const close = useCallback(() => setOpen(false), []);

  const go = useCallback((path) => {
    setOpen(false);
    navigate(path);
  }, [navigate]);

  const toggleLanguage = useCallback(async () => {
    const next = isSpanish ? "en" : "es";
    try {
      i18n.changeLanguage(next);
      try { await api.patch("/auth/me", { language: next }); } catch { /* offline OK */ }
      toast.success(next === "es" ? L.langSwitchedEs : L.langSwitchedEn);
    } finally {
      setOpen(false);
    }
  }, [isSpanish, i18n, L]);

  const handleSignOut = useCallback(async () => {
    try {
      toast.info(L.signingOut);
      await logout?.();
      navigate("/");
    } catch (e) {
      /* swallow; navigation will take user back to landing */
    } finally {
      setOpen(false);
    }
  }, [logout, navigate, L]);

  // Context-aware quick actions (only relevant ones surface).
  const onDealDetail = /^\/deals\/[^/]+$/.test(location.pathname) && !location.pathname.endsWith("/new");
  const onCompare = location.pathname === "/compare";
  const partnershipMatch = location.pathname.match(/^\/syndication\/partnerships\/([^/]+)/);
  const onPartnershipDetail = !!partnershipMatch;
  const currentPartnershipId = partnershipMatch?.[1] || null;
  const currentDealId = onDealDetail ? location.pathname.split("/")[2] : null;

  const copyLastInviteLink = useCallback(async () => {
    if (!currentPartnershipId) return;
    try {
      const detail = await api.get(`/syndication/partnerships/${currentPartnershipId}`);
      const pending = (detail.data?.members || []).filter(
        (m) => m.invite_status === "pending" && m.role !== "sponsor",
      );
      if (!pending.length) {
        toast.info(L.inviteLinkNoneAvailable);
        return;
      }
      const lastPending = pending[pending.length - 1];
      const res = await api.get(
        `/syndication/partnerships/${currentPartnershipId}/members/${lastPending.id}/invite-link`,
      );
      const url = res.data?.invite_url;
      if (!url) {
        toast.error(L.inviteLinkError);
        return;
      }
      try {
        await navigator.clipboard.writeText(url);
        toast.success(L.inviteLinkCopied);
      } catch {
        // Some browsers gate clipboard access — at least show the URL.
        toast.success(`${L.inviteLinkCopied}: ${url}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || L.inviteLinkError);
    } finally {
      setOpen(false);
    }
  }, [currentPartnershipId, L]);

  if (!user) return null; // never show on logged-out pages

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-[120] flex items-start justify-center bg-slate-900/40 px-4 pt-[14vh] backdrop-blur-sm"
          onClick={(e) => e.target === e.currentTarget && close()}
          data-testid="command-palette-backdrop"
        >
          <Command
            label="Command palette"
            shouldFilter={true}
            className="w-full max-w-xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
            data-testid="command-palette"
          >
            <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
              <Search className="h-4 w-4 text-slate-400" />
              <Command.Input
                ref={inputRef}
                value={query}
                onValueChange={setQuery}
                placeholder={L.placeholder}
                className="w-full bg-transparent text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
                data-testid="command-palette-input"
              />
              <kbd className="hidden rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 sm:inline">
                {KEYBINDING_HINT}
              </kbd>
            </div>

            <Command.List
              className="max-h-[60vh] overflow-y-auto p-2"
              data-testid="command-palette-list"
            >
              <Command.Empty
                className="px-4 py-8 text-center text-sm text-slate-500"
                data-testid="command-palette-empty"
              >
                {L.empty}
              </Command.Empty>

              {/* Navigate */}
              <Command.Group heading={L.nav} className="mb-1">
                <CmdItem icon={LayoutDashboard} label={L.dashboard} keywords={["dashboard","home","start"]} onSelect={() => go("/dashboard")} testid="cp-nav-dashboard" />
                <CmdItem icon={Plus} label={L.newDeal} keywords={["new","add","create","deal","property"]} onSelect={() => go("/deals/new")} testid="cp-nav-new-deal" />
                <CmdItem icon={Briefcase} label={L.portfolio} keywords={["portfolio","owned","properties"]} onSelect={() => go("/portfolio")} testid="cp-nav-portfolio" />
                <CmdItem icon={GitCompare} label={L.compare} keywords={["compare","side by side","vs"]} onSelect={() => go("/compare")} testid="cp-nav-compare" />
                <CmdItem icon={Bot} label={L.copilot} keywords={["copilot","ai","assistant","chat"]} onSelect={() => go("/copilot")} testid="cp-nav-copilot" />
                <CmdItem
                  icon={Sparkles}
                  label={L.askCopilot}
                  keywords={["ask","copilot","ai","new","start","sparkles","question"]}
                  onSelect={() => {
                    close();
                    if (location.pathname !== "/copilot") {
                      navigate("/copilot");
                    }
                    // Fire after navigation so Copilot.jsx is mounted.
                    setTimeout(() => {
                      window.dispatchEvent(new CustomEvent("runidea:copilot-new"));
                    }, 100);
                  }}
                  testid="cmdk-action-copilot-ask"
                />
                <CmdItem
                  icon={Bot}
                  label={L.continueCopilot}
                  keywords={["continue","resume","copilot","ai","last","previous"]}
                  onSelect={() => {
                    close();
                    if (location.pathname !== "/copilot") {
                      navigate("/copilot");
                    }
                    setTimeout(() => {
                      window.dispatchEvent(new CustomEvent("runidea:copilot-continue"));
                    }, 100);
                  }}
                  testid="cmdk-action-copilot-continue"
                />
                <CmdItem icon={FileBarChart2} label={L.reports} keywords={["reports","exports","pdf"]} onSelect={() => go("/reports")} testid="cp-nav-reports" />
                <CmdItem icon={SettingsIcon} label={L.settings_label} keywords={["settings","preferences","account"]} onSelect={() => go("/settings")} testid="cp-nav-settings" />
              </Command.Group>

              {/* Recent deals */}
              {recentDeals.length > 0 && (
                <Command.Group heading={L.recentDeals} className="mb-1">
                  {recentDeals.map((d) => {
                    const addr = d.property?.address || d.address || d.title || d.id;
                    // Compact detail string (drops sqft/rent so we stay
                    // single-row inside the palette). Falls back to city
                    // alone if no property fields resolved.
                    const detail = propertyDetailLineCompact(d) || d.property?.city || d.city || "";
                    return (
                      <CmdItem
                        key={d.id}
                        icon={HomeIcon}
                        label={addr}
                        hint={detail}
                        keywords={[addr, detail, d.id, d.deal_type].filter(Boolean)}
                        onSelect={() => go(`/deals/${d.id}`)}
                        testid={`cp-recent-${d.id}`}
                      />
                    );
                  })}
                </Command.Group>
              )}

              {/* Phase 34c.3 — Syndication actions (always-on + context-aware) */}
              <Command.Group heading="Syndication" className="mb-1">
                <CmdItem
                  icon={Wallet}
                  label={L.investorPortal}
                  keywords={["investor","portal","lp","cockpit","syndication"]}
                  onSelect={() => go("/investor")}
                  testid="cmdk-action-investor-portal"
                />
                <CmdItem
                  icon={FileBarChart2}
                  label={L.yearEndReport}
                  keywords={["year","end","annual","report","pdf","investor","2026","2025","narrative"]}
                  onSelect={async () => {
                    close();
                    // Direct GET download (no navigation) — works from anywhere.
                    try {
                      const y = new Date().getFullYear();
                      const url = `/api/reports/year-end/export.pdf?year=${y}`;
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `RunIdea_YearEnd_${y}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                    } catch { /* ignore */ }
                  }}
                  testid="cmdk-action-year-end-report"
                />
                {onDealDetail && currentDealId && (
                  <CmdItem
                    icon={Users2}
                    label={L.createPartnership}
                    keywords={["partnership","syndicate","raise","lp","deal","create"]}
                    onSelect={() => {
                      close();
                      window.dispatchEvent(new CustomEvent("runidea:open-create-partnership", {
                        detail: { dealId: currentDealId },
                      }));
                    }}
                    testid="cmdk-action-syndication-create-partnership"
                  />
                )}
                {onPartnershipDetail && (
                  <CmdItem
                    icon={Coins}
                    label={L.computeDistribution}
                    keywords={["distribution","compute","waterfall","preview","run"]}
                    onSelect={() => {
                      close();
                      window.dispatchEvent(new CustomEvent("runidea:open-compute-distribution", {
                        detail: { partnershipId: currentPartnershipId },
                      }));
                    }}
                    testid="cmdk-action-syndication-compute-distribution"
                  />
                )}
                {onPartnershipDetail && (
                  <CmdItem
                    icon={Link2}
                    label={L.copyInviteLink}
                    keywords={["invite","link","share","copy","lp","member"]}
                    onSelect={copyLastInviteLink}
                    testid="cmdk-action-syndication-copy-last-invite-link"
                  />
                )}
              </Command.Group>

              {/* Context-aware actions */}
              {(onDealDetail || onCompare) && (
                <Command.Group heading={L.actions} className="mb-1">
                  {onDealDetail && (
                    <CmdItem
                      icon={Sparkles}
                      label={L.markBought}
                      keywords={["mark","bought","owned","purchase","close"]}
                      onSelect={() => {
                        close();
                        // Broadcast: DealDetail listens for this and opens
                        // its MarkAsBoughtModal.
                        window.dispatchEvent(new CustomEvent("runidea:open-mark-bought"));
                      }}
                      testid="cp-action-mark-bought"
                    />
                  )}
                  {onCompare && (
                    <CmdItem
                      icon={FileBarChart2}
                      label={L.exportCompare}
                      keywords={["export","excel","xlsx","compare"]}
                      onSelect={() => {
                        close();
                        window.dispatchEvent(new CustomEvent("runidea:export-compare-xlsx"));
                      }}
                      testid="cp-action-export-compare"
                    />
                  )}
                </Command.Group>
              )}

              {/* Admin */}
              {user.is_admin && (
                <Command.Group heading={L.admin} className="mb-1">
                  <CmdItem icon={Shield} label={L.searchUsers} keywords={["users","accounts","customers","admin"]} onSelect={() => go("/admin/users")} testid="cp-admin-users" />
                  <CmdItem icon={KeyRound} label={L.auditLog} keywords={["audit","log","trace","history"]} onSelect={() => go("/admin/audit-log")} testid="cp-admin-audit-log" />
                  <CmdItem icon={Bot} label={L.aiUsage} keywords={["ai","usage","cost","model"]} onSelect={() => go("/admin/ai-calls")} testid="cp-admin-ai-usage" />
                </Command.Group>
              )}

              {/* Settings */}
              <Command.Group heading={L.settings}>
                <CmdItem icon={Languages} label={L.toggleLang} keywords={["language","idioma","spanish","english","translate"]} onSelect={toggleLanguage} testid="cp-settings-lang" />
                <CmdItem icon={LogOut} label={L.signOut} keywords={["logout","sign out","exit","quit"]} onSelect={handleSignOut} testid="cp-settings-signout" />
              </Command.Group>
            </Command.List>

            <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-3 py-1.5 text-[10px] text-slate-500">
              <span>{L.hintNav}</span>
              <span>{L.hintEnter}</span>
              <span>{L.hintEsc}</span>
            </div>
          </Command>
        </div>
      )}
    </>
  );
}

function CmdItem({ icon: Icon, label, hint, keywords = [], onSelect, testid }) {
  return (
    <Command.Item
      onSelect={onSelect}
      data-testid={testid}
      value={[label, ...keywords].filter(Boolean).join(" ")}
      className="group flex cursor-pointer select-none items-center gap-3 rounded-md px-3 py-2 text-sm text-slate-700 aria-selected:bg-indigo-50 aria-selected:text-indigo-700"
    >
      {Icon ? <Icon className="h-4 w-4 shrink-0" /> : <span className="h-4 w-4 shrink-0" />}
      <span className="flex-1 truncate">{label}</span>
      {hint && <span className="truncate text-xs text-slate-400">{hint}</span>}
      <ArrowRight className="h-3 w-3 opacity-0 transition-opacity group-aria-selected:opacity-100" />
    </Command.Item>
  );
}
